// @flow
import logStats from 'app/common/lib/logStats';
import NGJS_RESOURCES from 'app/packages/full-node-utils/ngjsResources.json';

type ExperimentActivatorFn = (string) => ?string;

export const RESOURCE_URL_NODE = (name: string, method: string) =>
  `/_ngjs/resource/${name}/${method}/`;

export const RESOURCE_URL_PYTHON = (name: string, method: string) => `/resource/${name}/${method}/`;

export function isResourceInFile(name: string, ngjsResources: {}) {
  return Object.prototype.hasOwnProperty.call(ngjsResources, name);
}

export function isNgjsResource(name: string, ngjsResources: {}) {
  return Boolean(isResourceInFile(name, ngjsResources) && ngjsResources[name].isNode);
}

// isNode=true and isPython=false:
// usually indicate a fully migrated resource available only in node
export function isNgjsOnlyResource(name: string, ngjsResources: {}) {
  return Boolean(isNgjsResource(name, ngjsResources) && !ngjsResources[name].isPython);
}

// isNode=true and isPython=true: usually indicate a resource being migrated
// and is usually accompanied by an experiment (enabled will cause the resource to be served
// from node). Without experiment it means we are waiting for in-process calls from python to die
// down before deleting the python resource
export function isMigrationResource(name: string, ngjsResources: {}): boolean {
  return Boolean(isNgjsResource(name, ngjsResources) && ngjsResources[name].isPython);
}

export function hasRegularExperimentConfigured(resourceName: string, ngjsResources: {}): boolean {
  return Boolean(ngjsResources[resourceName] && ngjsResources[resourceName].experiment);
}

export function hasSeoOrUnauthExperimentConfigured(
  resourceName: string,
  ngjsResources: {},
): boolean {
  return Boolean(ngjsResources[resourceName] && ngjsResources[resourceName].seoOrUnauthExperiment);
}

export function hasExperimentsConfigured(resourceName: string, ngjsResources: {}): boolean {
  return (
    hasRegularExperimentConfigured(resourceName, ngjsResources) ||
    hasSeoOrUnauthExperimentConfigured(resourceName, ngjsResources)
  );
}

function isExperimentEnabled({
  experimentActivator,
  isAuthenticated,
  isBot,
  ngjsResources,
  resourceName,
  seoOrUnauthExperimentActivator,
}: {|
  experimentActivator: ExperimentActivatorFn,
  isAuthenticated: boolean,
  isBot: boolean,
  ngjsResources: {},
  resourceName: string,
  seoOrUnauthExperimentActivator?: ExperimentActivatorFn,
|}) {
  const experimentName = ngjsResources[resourceName] && ngjsResources[resourceName].experiment;
  const seoOrUnauthExperimentName =
    ngjsResources[resourceName] && ngjsResources[resourceName].seoOrUnauthExperiment;

  let experiment;
  let group;

  if (seoOrUnauthExperimentActivator && seoOrUnauthExperimentName && (!isAuthenticated || isBot)) {
    experiment = seoOrUnauthExperimentName;
    group = seoOrUnauthExperimentActivator(seoOrUnauthExperimentName);
  } else {
    experiment = experimentName;
    group = experimentActivator(experimentName);
  }

  logStats.increment('resource_factory.is_enabled_group', 1, {
    isAuthenticated,
    isBot,
    experimentName: experiment,
    group: group || 'unknown',
    resourceName,
    statsGroup: 'ngjs',
  });

  return group && (group.startsWith('enabled') || group.startsWith('employees'));
}

export function isEligibleForNgjs({
  experimentActivator,
  isAuthenticated,
  isBot,
  ngjsResources = NGJS_RESOURCES,
  resourceName,
  seoOrUnauthExperimentActivator,
}: {|
  experimentActivator: ExperimentActivatorFn,
  isAuthenticated: boolean,
  isBot: boolean,
  resourceName: string,
  ngjsResources?: {},
  seoOrUnauthExperimentActivator?: ExperimentActivatorFn,
|}) {
  return Boolean(
    isNgjsOnlyResource(resourceName, ngjsResources) ||
      (isMigrationResource(resourceName, ngjsResources) &&
        (!hasExperimentsConfigured(resourceName, ngjsResources) ||
          isExperimentEnabled({
            experimentActivator,
            isAuthenticated,
            isBot,
            ngjsResources,
            resourceName,
            seoOrUnauthExperimentActivator,
          }))),
  );
}

export function getResourceUrl({
  experimentActivator,
  isAuthenticated,
  isBot,
  resourceName,
  method,
  ngjsResources = NGJS_RESOURCES,
  seoOrUnauthExperimentActivator,
}: {|
  experimentActivator: ExperimentActivatorFn,
  isAuthenticated: boolean,
  isBot: boolean,
  resourceName: string,
  method: string,
  ngjsResources?: {},
  seoOrUnauthExperimentActivator?: ExperimentActivatorFn,
|}): string {
  return isEligibleForNgjs({
    experimentActivator,
    isAuthenticated,
    isBot,
    resourceName,
    ngjsResources,
    seoOrUnauthExperimentActivator,
  })
    ? RESOURCE_URL_NODE(resourceName, method)
    : RESOURCE_URL_PYTHON(resourceName, method);
}
